/**
 * Characters — one to nine photos of a model, built into the four reference
 * shots every render reads identity from.
 *
 * Four steps, because they are a real sequence: photos have to exist before a
 * reference can be built from them, a build has to finish before it can be
 * judged, and judging is the gate — a set is the character's identity for every
 * render that follows, so a drifted reference poisons everything made after it.
 *
 * Photos live client-side until step 1 is committed. That is deliberate: a
 * reference attaches to a character id, and creating the character on entry
 * would leave an "Untitled" behind every time someone opened this and changed
 * their mind.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Base, type Character, type CharacterReference, type Photo } from '../lib/api';
import { openFluidCloudPicker, fetchPicked } from '../lib/fluidcloudPicker';
import Img from '../components/Img';
import FluidCloudMark from '../components/FluidCloudMark';
import { Places, Wardrobe } from './World';
import Days from './Days';
import './characters.css';
import './sets.css';

/** Nine — the provider takes ten images and the mannequin base claims the tenth. */
const MAX_PHOTOS = 9;
/** Measured: a 2K reference is $0.09 and lands in 90-140s. */
const PER_REFERENCE = 0.09;

const STEPS = ['Photos', 'Build references', 'Review', 'Identity set'] as const;

/** A photo in the grid, whether it exists on the server yet or not. */
interface Slot {
  key: string;
  src: string | null;
  /** Set once it lives on the server. */
  id?: string;
  /** Set while it is still only in the browser. */
  file?: File;
}

export interface CharactersProps {
  characters: Character[];
  onChanged: () => void;
  onUse: (id: string) => void;
}

export default function Characters({ characters, onChanged, onUse }: CharactersProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [pending, setPending] = useState<Slot[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [bases, setBases] = useState<Base[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fileOver, setFileOver] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  /* Thumbnails are small by necessity; the full-size look is one click away.
     `raw` marks a browser-only object URL, which does not go through <Img>. */
  const [preview, setPreview] = useState<{ src: string | null; label: string; raw?: boolean } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  /* Identity is what the wizard already builds; the other three are her world. */
  const [section, setSection] = useState<'identity' | 'places' | 'wardrobe' | 'days'>('identity');
  const open = characters.find((c) => c.id === openId) ?? null;

  useEffect(() => {
    void api.get<{ bases: Base[] }>('/bases').then((r) => setBases(r.bases)).catch(() => undefined);
  }, []);

  /* Object URLs for photos that only exist in the browser. Revoked when their
     tile goes away or the screen unmounts — NOT on every change to `pending`,
     which revoked the URLs of photos still on screen the moment another was
     added, and blanked them. */
  const objectUrls = useRef<string[]>([]);
  useEffect(() => () => { objectUrls.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const slots: Slot[] = useMemo(
    () => (open ? open.photos.map((p: Photo) => ({ key: p.id, id: p.id, src: p.src })) : pending),
    [open, pending],
  );

  const refs = open?.references ?? [];
  const building = refs.some((r) => r.status === 'running');
  const built = refs.filter((r) => r.status === 'done');
  const approved = built.filter((r) => r.approved);

  /* A build finishes on the server, so poll while any is out. */
  useEffect(() => {
    if (!building) return;
    const timer = setInterval(() => onChanged(), 3000);
    return () => clearInterval(timer);
  }, [building, onChanged]);

  const guard = useCallback(async (fn: () => Promise<void>) => {
    setError(null); setBusy(true);
    try { await fn(); onChanged(); }
    catch (err) { setError(err instanceof Error ? err.message : 'That did not work'); }
    finally { setBusy(false); }
  }, [onChanged]);

  function startNew() {
    setOpenId(null); setPending([]); setName(''); setDescription('');
    setNotes({}); setError(null); setStep(1); setWizardOpen(true); setSection('identity');
  }

  function openExisting(c: Character) {
    setOpenId(c.id); setPending([]); setName(c.name); setDescription(c.description);
    setNotes({}); setError(null); setWizardOpen(true); setSection('identity');
    const done = c.references.filter((r) => r.status === 'done');
    setStep(c.photos.length === 0 ? 1 : done.length === 0 ? 2 : done.every((r) => r.approved) ? 4 : 3);
  }

  /* ------------------------------------------------------------ photos --- */

  function addFiles(files: FileList | File[]) {
    const room = Math.max(0, MAX_PHOTOS - slots.length);
    const images = Array.from(files).filter((f) => f.type.startsWith('image/')).slice(0, room);
    if (images.length === 0) return;
    if (open) {
      void guard(async () => {
        for (const file of images) {
          const form = new FormData();
          form.append('file', file);
          await api.upload(`/characters/${open.id}/photos`, form);
        }
      });
    } else {
      const made = images.map((file, i) => {
        const src = URL.createObjectURL(file);
        objectUrls.current.push(src);
        return { key: `local:${Date.now()}:${i}:${file.name}`, src, file };
      });
      setPending((prev) => [...prev, ...made]);
    }
  }

  async function importFromCloud() {
    setError(null);
    try {
      await openFluidCloudPicker({
        multiple: true,
        accept: 'image/*',
        onPicked: (items) => {
          void (async () => {
            const room = Math.max(0, MAX_PHOTOS - slots.length);
            const picked: File[] = [];
            for (const item of items.slice(0, room)) picked.push(await fetchPicked(item));
            addFiles(picked);
          })();
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open FluidCloud');
    }
  }

  function removeSlot(slot: Slot) {
    if (slot.id && open) {
      void guard(() => api.del(`/characters/${open.id}/photos/${slot.id}`));
    } else {
      if (slot.src) {
        URL.revokeObjectURL(slot.src);
        objectUrls.current = objectUrls.current.filter((u) => u !== slot.src);
      }
      setPending((prev) => prev.filter((p) => p.key !== slot.key));
    }
  }

  /** Order is not cosmetic: photo 1 leads and the prompt refers to them by number. */
  function move(fromKey: string, toKey: string) {
    if (fromKey === toKey) return;
    const keys = slots.map((s) => s.key);
    const from = keys.indexOf(fromKey);
    const to = keys.indexOf(toKey);
    if (from < 0 || to < 0) return;
    const next = [...slots];
    next.splice(to, 0, ...next.splice(from, 1));
    if (open) {
      void guard(() => api.patch(`/characters/${open.id}/photos/order`,
        { ids: next.map((s) => s.id) }).then(() => undefined));
    } else {
      setPending(next);
    }
  }

  function nudge(key: string, dir: -1 | 1) {
    const keys = slots.map((s) => s.key);
    const i = keys.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= keys.length) return;
    move(key, keys[j]!);
  }

  /* ------------------------------------------------------------- steps --- */

  /**
   * Commit step 1: create the character if it is new, then upload the photos.
   *
   * The new id is recorded the moment the character exists, BEFORE any photo is
   * sent. Without that, a photo failing here left a character nobody had a
   * handle on, and pressing Continue again made a second one.
   *
   * Photos leave `pending` only once they are actually stored, so a retry
   * resumes instead of uploading the same file twice, and the error names the
   * file that stopped it.
   */
  async function commitPhotos() {
    if (name.trim().length === 0 || slots.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      let id = open?.id;
      if (!id) {
        const res = await api.post<{ character: Character }>('/characters', {
          name: name.trim(), description: description.trim(),
        });
        id = res.character.id;
        setOpenId(id);
      } else if (name.trim() !== open?.name || description.trim() !== open?.description) {
        await api.patch(`/characters/${id}`, { name: name.trim(), description: description.trim() });
      }

      const queue = [...pending];
      for (let i = 0; i < queue.length; i++) {
        const slot = queue[i]!;
        if (!slot.file) continue;
        try {
          const form = new FormData();
          form.append('file', slot.file);
          await api.upload(`/characters/${id}/photos`, form);
        } catch (err) {
          setPending(queue.slice(i));            // keep this one and the rest
          const why = err instanceof Error ? err.message : 'unknown error';
          throw new Error(`"${slot.file.name}" did not upload — ${why}`);
        }
      }
      setPending([]);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  function build(base: Base) {
    if (!open) return;
    void guard(() => api.post(`/characters/${open.id}/references`, {
      base: base.id, note: notes[base.id] ?? refFor(base.id)?.note ?? '',
    }).then(() => undefined));
  }

  function approve(base: Base) {
    if (!open) return;
    void guard(() => api.post(`/characters/${open.id}/references/${base.id}/approve`).then(() => undefined));
  }

  function refFor(baseId: string): CharacterReference | undefined {
    return refs.find((r) => r.base === baseId);
  }

  /* Real seconds since the build started — not a guess, not a bare spinner. */
  const [, tick] = useState(0);
  useEffect(() => {
    if (!building) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [building]);
  function elapsed(ref?: CharacterReference): number {
    if (!ref?.startedAt) return 0;
    return Math.max(0, Math.round((Date.now() - new Date(ref.startedAt).getTime()) / 1000));
  }
  /* Paced against the ~90s a build takes, capped short of full: the provider
     reports no progress, so finishing the bar early would be a lie. */
  function progress(ref?: CharacterReference): number {
    return Math.min(95, Math.round((elapsed(ref) / 90) * 100));
  }

  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types ?? []).includes('Files');
  const canContinue = name.trim().length > 0 && slots.length > 0;

  /* -------------------------------------------------------------- list --- */

  if (!wizardOpen) {
    return (
      <div className="pad">
        <div className="page-head">
          <div>
            <h2>Characters</h2>
            <p>
              One to nine photos of your model, put into fixed mannequin poses to build the four
              reference shots every render reads identity from.
            </p>
          </div>
          <div className="spacer">
            <button className="btn btn-primary" onClick={startNew}>New character</button>
          </div>
        </div>

        {error && <div className="notice bad" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="char-grid">
          {characters.map((c) => {
            const done = c.references.filter((r) => r.status === 'done');
            const ok = done.filter((r) => r.approved).length;
            return (
              <button key={c.id} className="card char-card" onClick={() => openExisting(c)}>
                <Img
                  src={done[0]?.src ?? c.photos[0]?.src} alt={c.name}
                  style={{ width: '100%', aspectRatio: '3/4', borderRadius: 0 }}
                />
                <span className="b">
                  <span className="nm">{c.name}</span>
                  <span className={`pill${ok === 4 ? ' ok' : ''}`} style={{ fontSize: 10 }}>
                    <span className="dot" />{ok} of 4
                  </span>
                </span>
              </button>
            );
          })}
          {characters.length === 0 && (
            <div className="notice" style={{ gridColumn: '1 / -1' }}>
              No characters yet. Create one, give it a photo of your model, and build its reference set.
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------ wizard --- */

  return (
    <div className="pad">
      <div className="page-head">
        <div>
          <h2>{open ? open.name : 'Create a character'}</h2>
          <p>
            Give us between one and nine photos of your model. We put them into fixed mannequin poses
            to build the four reference shots every render reads identity from — so every character is
            framed identically, whoever made it.
          </p>
        </div>
        <div className="spacer">
          <button className="btn" onClick={() => { setWizardOpen(false); setError(null); }}>
            All characters
          </button>
        </div>
      </div>

      {open && (
        <div className="sections">
          {(['identity', 'places', 'wardrobe', 'days'] as const).map((id) => (
            <button
              key={id}
              {...(section === id ? { 'aria-current': 'page' as const } : {})}
              onClick={() => setSection(id)}
            >{id[0]!.toUpperCase() + id.slice(1)}</button>
          ))}
        </div>
      )}

      {open && section === 'places' && <Places characterId={open.id} characterName={open.name} />}
      {open && section === 'wardrobe' && <Wardrobe characterId={open.id} characterName={open.name} />}
      {open && section === 'days' && <Days characterId={open.id} />}

      {section === 'identity' && (<>
      <ol className="steps">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const reachable = n === 1 || Boolean(open);
          return (
            <li key={label}>
              <button
                className={n < step ? 'done' : undefined}
                disabled={!reachable}
                {...(n === step ? { 'aria-current': 'step' as const } : {})}
                onClick={() => reachable && setStep(n)}
              >
                <span className="n">{n}</span>{label}
              </button>
            </li>
          );
        })}
      </ol>

      {error && <div className="notice bad" style={{ marginBottom: 16 }}>{error}</div>}

      {/* ─────────── 1 · photos ─────────── */}
      {step === 1 && (
        <div className="wiz-split">
          <div className="card">
            <div className="sect-head">
              <span className="eyebrow">Photos of your model</span>
              <span className="pill mono" style={{ fontSize: 10.5 }}>
                {slots.length} of {MAX_PHOTOS} · {MAX_PHOTOS - slots.length} slots left
              </span>
            </div>

            <div
              className={`src-grid${fileOver ? ' file-over' : ''}`}
              onDragOver={(e) => { if (!isFileDrag(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setFileOver(true); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFileOver(false); }}
              onDrop={(e) => { if (!isFileDrag(e)) return; e.preventDefault(); setFileOver(false); addFiles(e.dataTransfer.files); }}
            >
              {slots.map((slot, i) => (
                <div
                  key={slot.key}
                  className={[
                    'src-tile', i === 0 ? 'lead' : '',
                    dragKey === slot.key ? 'dragging' : '',
                    dropKey === slot.key && dragKey !== slot.key ? 'dropping' : '',
                  ].filter(Boolean).join(' ')}
                  draggable tabIndex={0} title="Drag to reorder"
                  onDragStart={(e) => {
                    setDragKey(slot.key);
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox will not begin a drag unless the transfer carries
                    // something; every other browser ignores this.
                    e.dataTransfer.setData('text/plain', slot.key);
                  }}
                  onDragOver={(e) => {
                    if (isFileDrag(e)) return;      // dropped photos belong to the grid
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDropKey(slot.key);
                  }}
                  onDragLeave={() => setDropKey((k) => (k === slot.key ? null : k))}
                  onDrop={(e) => {
                    if (isFileDrag(e)) return;
                    e.preventDefault();
                    if (dragKey) move(dragKey, slot.key);
                    setDragKey(null); setDropKey(null);
                  }}
                  onDragEnd={() => { setDragKey(null); setDropKey(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(slot.key, -1); }
                    if (e.key === 'ArrowRight') { e.preventDefault(); nudge(slot.key, 1); }
                  }}
                >
                  <button
                    className="peek" title="See it full size"
                    onClick={() => setPreview({ src: slot.src, label: `Photo ${i + 1}`, raw: Boolean(slot.file) })}
                  >
                    {slot.file
                      ? <img className="raw" src={slot.src ?? ''} alt={`Photo ${i + 1}`} />
                      : <Img src={slot.src} alt={`Photo ${i + 1}`} />}
                  </button>
                  <span className="idx">{i + 1}</span>
                  <button className="kill" title="Remove" disabled={busy}
                    onClick={(e) => { e.stopPropagation(); removeSlot(slot); }}>×</button>
                  {i === 0 && <span className="tag">leads</span>}
                </div>
              ))}

              {slots.length < MAX_PHOTOS && (
                <div className="src-add">
                  <button className="main" disabled={busy} onClick={() => fileRef.current?.click()}>
                    {busy ? <span className="spin" /> : <span style={{ fontSize: 21 }}>+</span>}
                    <span>{busy ? 'Adding…' : 'Add photo'}</span>
                    <span className="sub">drag in, or browse<br />your machine</span>
                  </button>
                  <button className="cloud" disabled={busy} onClick={() => void importFromCloud()}>
                    <FluidCloudMark size={17} />
                    FluidCloud
                  </button>
                </div>
              )}
            </div>

            <div className="notice" style={{ marginTop: 15 }}>
              <b>One is enough, nine is the ceiling.</b> Each reference is a single call carrying your
              photos plus one mannequin base — and the service takes ten images per call, so the base
              always claims the tenth. More angles of the same face give the build more to work from;
              photos in different lighting or with a different haircut give it less.
            </div>
            <p className="faint" style={{ fontSize: 11.5, lineHeight: 1.6, margin: '11px 0 0' }}>
              Photo 1 leads — it is the one the build treats as the primary likeness. Drag a tile to
              reorder, or focus one and use ← →.
            </p>
            <input
              ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden multiple
              onChange={(e) => {
                // Copy the FileList out BEFORE resetting the input: it is LIVE,
                // and clearing value empties the very list we are holding, so
                // the handler saw zero files and did nothing — silently.
                const picked = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (picked.length) addFiles(picked);
              }}
            />
          </div>

          <div className="card">
            <div className="wiz-field">
              <label className="eyebrow" htmlFor="nm">Name</label>
              <input className="field" id="nm" value={name} maxLength={80}
                placeholder="Who is this?" onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="wiz-field">
              <label className="eyebrow" htmlFor="ds">Description — optional</label>
              <textarea className="field" id="ds" rows={3} style={{ resize: 'vertical' }}
                maxLength={600} value={description} onChange={(e) => setDescription(e.target.value)} />
              <p className="faint" style={{ fontSize: 11.5, lineHeight: 1.6, margin: '2px 0 0' }}>
                Appended to every prompt that uses them. Not the same as the per-reference notes on
                the next step, which steer one build only.
              </p>
            </div>
            <div className="notice">
              <div className="kv">
                <dt>References</dt><dd>4</dd>
                <dt>Cost</dt><dd>${(PER_REFERENCE * 4).toFixed(2)}</dd>
                <dt>Each</dt><dd>~90s</dd>
              </div>
            </div>
            <div className="foot">
              <button className="btn btn-primary" disabled={busy || !canContinue} onClick={() => void commitPhotos()}>
                {busy ? <><span className="spin" /> Saving…</> : 'Continue'}
              </button>
              {!canContinue && (
                <span className="faint" style={{ fontSize: 11.5 }}>
                  {name.trim() ? 'Add at least one photo.' : 'Give them a name.'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─────────── 2 · build ─────────── */}
      {step === 2 && open && (
        <div className="card">
          <div className="sect-head">
            <div style={{ flex: 1 }}>
              <span className="eyebrow">Build references</span>
              <p className="muted" style={{ margin: '7px 0 0', fontSize: 13, maxWidth: '74ch' }}>
                Send each one when you're ready. Nothing fires on its own — build the front face
                first, see whether the likeness holds, and only then spend on the rest. Add a note to
                any reference to change what that one build produces.
              </p>
            </div>
            <span className={`pill${built.length === 4 ? ' ok' : building ? ' accent' : ''}`}>
              {building ? <span className="spin" /> : <span className="dot" />}
              {built.length} of 4 built
            </span>
          </div>

          <div className="refs">
            {bases.map((base) => {
              const ref = refFor(base.id);
              const state = ref?.status;
              return (
                <div key={base.id} className={`ref-row${state === 'done' ? ' done' : state === 'failed' ? ' failed' : ''}`}>
                  <div className="ref-main">
                    <button
                      className="thumb base-thumb" title="See the mannequin base full size"
                      onClick={() => setPreview({ src: base.src, label: `${base.label} — mannequin base` })}
                    >
                      <Img src={base.src} alt={`${base.label} base`} />
                    </button>
                    <button
                      className="thumb out" title={ref?.src ? 'See it full size' : undefined}
                      disabled={!ref?.src}
                      onClick={() => ref?.src && setPreview({ src: ref.src, label: base.label })}
                    >
                      {ref?.src
                        ? <Img src={ref.src} alt={base.label} />
                        : <span className="frame" aria-hidden="true" />}
                    </button>
                    <div className="info">
                      <div className="nm">{base.label}</div>
                      <div className="sub">
                        {state === 'failed' ? (ref?.error ?? 'that build failed') : base.hint}
                      </div>
                      <div className="inputs">{slots.length} photo{slots.length === 1 ? '' : 's'} + 1 base &rarr; 1 reference</div>
                    </div>
                    <div className="bar">
                      <span style={{ width: state === 'done' ? '100%' : state === 'running' ? `${progress(ref)}%` : '0' }} />
                    </div>
                    <button
                      className={`btn btn-sm${state === 'done' ? '' : ' btn-primary'}`}
                      disabled={busy || state === 'running'}
                      onClick={() => build(base)}
                    >
                      {state === 'running' ? 'Building…' : state ? 'Rebuild' : 'Build'}
                    </button>
                    <div className="t cell" style={state === 'done' ? { color: 'var(--fd-ok)' } : undefined}>
                      {state === 'running' ? `${elapsed(ref)}s`
                        : state === 'done' ? 'ready'
                        : state === 'failed' ? 'failed' : 'not built'}
                    </div>
                    <div className="ref-note">
                      <label className="eyebrow" htmlFor={`n-${base.id}`}>Note</label>
                      <input
                        id={`n-${base.id}`} placeholder={base.notePlaceholder}
                        value={notes[base.id] ?? ref?.note ?? ''}
                        onChange={(e) => setNotes((n) => ({ ...n, [base.id]: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="notice" style={{ marginTop: 15 }}>
            <b>The note steers this one build only.</b> Body shape follows the photos you gave us, so
            use the note on the body reference to correct what they don't show — proportions, a tattoo
            to leave out, height against the base.
            <br /><br />
            Each build takes about a minute and a half, and the time varies a lot — 32s to 134s
            measured on identical calls. Sending them by hand means a bad likeness costs one call,
            not four.
          </div>
          <div className="foot">
            <button className="btn btn-primary" disabled={built.length === 0} onClick={() => setStep(3)}>
              Review {built.length} reference{built.length === 1 ? '' : 's'}
            </button>
            <span className="mono faint" style={{ fontSize: 11.5 }}>
              ${(built.length * PER_REFERENCE).toFixed(2)} spent
            </span>
          </div>
        </div>
      )}

      {/* ─────────── 3 · review ─────────── */}
      {step === 3 && open && (
        <div className="card">
          <div className="sect-head">
            <div style={{ flex: 1 }}>
              <span className="eyebrow">Review</span>
              <p className="muted" style={{ margin: '7px 0 0', fontSize: 13, maxWidth: '74ch' }}>
                The small tile on each shot is the mannequin base it was built from. Re-roll anything
                that drifted — this set is the character's identity from here on.
              </p>
            </div>
            <span className={`pill${approved.length === built.length && built.length > 0 ? ' ok' : ''}`}>
              <span className="dot" />{approved.length} of {built.length} kept
            </span>
          </div>

          <div className="review">
            {bases.map((base, i) => {
              const ref = refFor(base.id);
              return (
                <div key={base.id} className="ref-card">
                  <div className="shot">
                    {ref?.src
                      ? (
                        <button
                          className="peek" title="See it full size"
                          onClick={() => setPreview({ src: ref.src, label: base.label })}
                        >
                          <Img src={ref.src} alt={base.label} />
                        </button>
                      )
                      : <div className="ph">not built yet</div>}
                    <span className="slot">{i + 1} · {base.label.toLowerCase()}</span>
                    <img className="base" src={base.src} alt="" aria-hidden="true" />
                    {ref?.approved && <span className="kept">kept</span>}
                  </div>
                  <div className="meta">
                    <div className="nm">{base.label}</div>
                    <div className="acts">
                      <button className="btn btn-sm" disabled={busy || ref?.status === 'running'}
                        onClick={() => build(base)}>Re-roll</button>
                      <button
                        className={`btn btn-sm${ref?.approved ? '' : ' btn-primary'}`}
                        disabled={busy || ref?.status !== 'done' || ref?.approved}
                        onClick={() => approve(base)}
                      >
                        {ref?.approved ? 'Kept' : 'Keep'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="notice warn" style={{ marginTop: 16 }}>
            <b>Views your photos don't contain are invented.</b> If you only uploaded front-facing
            shots, the three-quarter and body references are plausible and internally consistent, but
            they are not derived from anything you gave us. Check them properly — every later render
            inherits whatever you keep.
          </div>
          <div className="foot">
            <button className="btn btn-primary" disabled={approved.length === 0} onClick={() => setStep(4)}>
              Save character
            </button>
            <button className="btn" onClick={() => setStep(2)}>Back</button>
            <span className="faint" style={{ fontSize: 11.5 }}>
              {approved.length === 0 ? 'Keep at least one reference to finish.' : ''}
            </span>
          </div>
        </div>
      )}

      {/* ─────────── 4 · identity set ─────────── */}
      {step === 4 && open && (
        <div className="card">
          <div className="sect-head">
            <div style={{ flex: 1 }}>
              <span className="eyebrow">Saved</span>
              <h3 style={{ fontSize: 19, marginTop: 7 }}>{open.name}</h3>
            </div>
            <span className={`pill${approved.length === 4 ? ' ok' : ''}`}>
              <span className="dot" />{approved.length} of 4 kept
            </span>
            <button className="btn btn-sm" disabled={approved.length === 0} onClick={() => onUse(open.id)}>
              Use in studio
            </button>
            <button
              className="btn btn-sm" disabled={busy}
              onClick={() => void guard(async () => {
                if (!window.confirm(`Delete ${open.name}, its photos and its references?`)) return;
                await api.del(`/characters/${open.id}`);
                setWizardOpen(false); setOpenId(null);
              })}
            >Delete</button>
          </div>

          <p className="muted" style={{ margin: '12px 0 16px', fontSize: 13, maxWidth: '74ch' }}>
            These go out with every render of {open.name}, always in this order. Nothing is chosen at
            render time, so there is no way for the wrong reference to be sent.
          </p>

          <div className="final">
            {bases.map((base, i) => {
              const ref = refFor(base.id);
              return (
                <figure key={base.id}>
                  <div className="fr">
                    {ref?.src && ref.approved
                      ? <Img src={ref.src} alt={base.label} />
                      : <div className="ph">{ref?.src ? 'not kept' : 'not built'}</div>}
                  </div>
                  <figcaption>
                    {base.label}
                    <span className="k">image {i + 1} · {base.hint}</span>
                  </figcaption>
                </figure>
              );
            })}
          </div>

          <div style={{ marginTop: 24 }}>
            <span className="eyebrow">What a render sends</span>
            <div className="slots">
              {['1', '2', '3', '4'].map((n, i) => (
                <div key={n} className={`slot-cell${i < approved.length ? ' id' : ''}`}>{n}</div>
              ))}
              <div className="slot-cell ctx">scene</div>
              <div className="slot-cell ctx">outfit</div>
              <div className="slot-cell ctx">place</div>
              <div className="slot-cell">·</div><div className="slot-cell">·</div><div className="slot-cell">·</div>
            </div>
            <div className="legend">
              <span><i style={{ background: 'rgba(239,113,52,.4)' }} />Identity — always these four</span>
              <span><i style={{ background: 'rgba(255,255,255,.14)' }} />Scene, outfit, place</span>
              <span><i style={{ background: 'rgba(255,255,255,.04)', border: '1px solid var(--fd-hair-3)' }} />Spare</span>
            </div>
            <p className="faint" style={{ fontSize: 11.5, lineHeight: 1.6, margin: '11px 0 0', maxWidth: '78ch' }}>
              Two budgets, both capped at ten: <b className="muted">building</b> a reference spends up to
              nine of your photos plus one mannequin base; <b className="muted">rendering</b> spends the
              identity references plus the scene, an outfit and a place.
            </p>
          </div>

          <div className="foot">
            <button className="btn" onClick={() => { setWizardOpen(false); setError(null); }}>
              All characters
            </button>
            <button className="btn" onClick={() => setStep(2)}>Build more</button>
          </div>
        </div>
      )}

      </>)}

      {preview && (
        <>
          <div className="lb-scrim" onClick={() => setPreview(null)} />
          <div className="lb" role="dialog" aria-label={preview.label}>
            <div className="lb-head">
              <span style={{ flex: 1, fontSize: 13 }}>{preview.label}</span>
              <button className="btn btn-sm" onClick={() => setPreview(null)}>Close</button>
            </div>
            <div className="lb-body">
              {preview.raw
                ? <img src={preview.src ?? ''} alt={preview.label} />
                : <Img src={preview.src} alt={preview.label} style={{ maxHeight: '78vh', width: 'auto' }} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
